import { FC, useMemo, useState } from "react";
import { Box, Typography, IconButton, Button, InputAdornment, TextField } from "@mui/material";
import theme from "@/theme";
import RemoveImgArticle from "@/assets/svg/RemoveImgArticle";
import { stylesPaidContentLine } from "./stylesPaidContentLine";
import CustomLoader from "../../CustomLoader/CustomLoader";
type Props = {
  price: string;
  onSubmit?: (amount: number) => void | Promise<void>;
  onHeaderClick: () => void;
  isLoading?: boolean;
};

const ArbitraryBuyModal: FC<Props> = ({ onSubmit, price, onHeaderClick, isLoading = false }) => {
  const normalizePrice = (value: string) => {
    let cleaned = value.replace(/[^\d.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot !== -1) {
      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
    }
    const [intPart, decPart] = cleaned.split(".");
    const normalizedInt = intPart.slice(0, 11);
    return decPart !== undefined ? `${normalizedInt}.${decPart.slice(0, 2)}` : normalizedInt;
  };
  const [amount, setAmount] = useState("");
  const isPriceValid = useMemo(() => {
    if (!amount) return false;
    const num = Number(amount);
    return Number.isFinite(num) && num >= Number(price);
  }, [amount, price]);
  const handleClick = () => {
    if (onSubmit && isPriceValid && !isLoading) {
      onSubmit(Number(amount));
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0 0 22px",
        borderRadius: "24px",
        width: "506px",
        boxShadow: "0px 0px 4px 0px #0000001A;",
        bgcolor: theme.palette.custom.mainWhite,
        fontFamily: "Inter, sans-serif",
        color: "#2A2D31",
        boxSizing: "border-box",
        outline: "none",
      }}
    >
      <Box
        sx={{
          width: "100%",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: "10px",
          mb: "32px",
        }}
      >
        <IconButton
          onClick={onHeaderClick}
          sx={{
            position: "absolute",
            right: "22px",
            top: "12px",
            color: "#52575D",
            padding: 0,
          }}
        >
          <RemoveImgArticle size={24} bgColor="#52575DB2" />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          width: "350px",
          mt: "22px",
          gap: "40px",
        }}
      >
        <Typography
          component="h1"
          variant="h2"
          fontSize="16px"
          fontWeight={600}
          lineHeight="100%"
          textAlign="center"
          color={theme.palette.custom.mainBlack}
          letterSpacing="2%"
        >
          Choose how much you want to pay to unlock the full content.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "center" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              textAlign: "center",
              color: theme.palette.custom.mainBlue,
            }}
          >
            Minimum amount to unlock: {price} USDC
          </Typography>
          <TextField
            variant="outlined"
            placeholder=""
            value={amount}
            onChange={(e) => {
              const normalized = normalizePrice(e.target.value);

              setAmount(normalized);
            }}
            sx={{
              ...stylesPaidContentLine.mainInput,
              "& .MuiOutlinedInput-root": {
                width: "286px",
                maxWidth: "286px",
                height: "48px",
                borderRadius: "30px",
                paddingRight: "4px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #4285f4",
                outline: "none",
                "&:hover": { border: "1px solid #4285f4" },
                "&.Mui-focused": { border: "1px solid #4285f4", outline: "none" },
                "& fieldset": { border: "none !important" },
                "&:hover fieldset": { border: "none !important" },
                "&.Mui-focused fieldset": { border: "none !important" },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "8px",
                    width: "100%",
                  }}
                >
                  <Button
                    onClick={handleClick}
                    variant="contained"
                    sx={{
                      ...stylesPaidContentLine.applyButton,
                      width: "93px",
                      mr: 0,
                    }}
                    disabled={!isPriceValid || isLoading}
                  >
                    {isLoading ? (
                      <CustomLoader
                        loading
                        size={20}
                        isModal={false}
                        sx={{ display: "inline-flex", verticalAlign: "middle" }}
                        color="#FFFFFF"
                      />
                    ) : (
                      "Pay"
                    )}
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ArbitraryBuyModal;
